import { useState, useRef, useEffect, useLayoutEffect, useMemo, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

export type SelectOptionStatus = 'open' | 'partial' | 'complete' | 'incomplete' | 'na'

export interface SelectOptionAction {
  label: string
  onClick: () => void
}

export interface SelectOption {
  id: string
  label: string
  group?: string
  disabled?: boolean
  status?: SelectOptionStatus
  hint?: string
  caption?: string
  actions?: SelectOptionAction[]
}

interface Props {
  options: SelectOption[]
  value: string | null
  onChange: (id: string | null, label: string | null) => void
  placeholder?: string
  disabled?: boolean
  allowCreate?: boolean
  onCreateNew?: (name: string) => Promise<SelectOption | null>
  createLabel?: string
  className?: string
  error?: boolean
  warning?: boolean
  /** When true, show a clear control to reset the selection to empty. */
  allowClear?: boolean
  /** Compact trigger sizing for dense inline table cells. */
  size?: 'default' | 'compact'
  /** When true, keep option order from props instead of sorting alphabetically. */
  preserveOrder?: boolean
  /** Prefer opening the menu below the trigger when space allows. */
  preferBelow?: boolean
  /** Use viewport bounds for placement instead of overflow clip ancestors. */
  useViewportForPlacement?: boolean
  /** Applied to the trigger button for label association (htmlFor). */
  id?: string
  /** When true, show hint/caption under the label in the trigger (opt-in stacked layout). */
  showDetailsInTrigger?: boolean
  /** When true, stretch trigger to fill parent cell height (table rows). */
  fillHeight?: boolean
  /** Fired for trigger and search-input keydown. If defaultPrevented, built-in keys are skipped. */
  onKeyDown?: (event: KeyboardEvent) => void
  /** Fired when the dropdown search text changes (for async option loading). */
  onSearchChange?: (query: string) => void
}

function OptionStatusCue({
  status,
  variant,
}: {
  status: SelectOptionStatus
  variant: 'list' | 'trigger'
}) {
  if (status === 'complete') {
    return (
      <CheckIcon
        className={`shrink-0 text-gray-400 ${variant === 'trigger' ? 'h-3.5 w-3.5' : 'mt-0.5 h-4 w-4'}`}
        aria-hidden
      />
    )
  }
  if (status === 'na') {
    return (
      <span
        className={`shrink-0 rounded bg-gray-100 px-1.5 font-medium text-gray-600 ${
          variant === 'trigger' ? 'py-0.5 text-[10px]' : 'py-0.5 text-xs'
        }`}
      >
        N/A
      </span>
    )
  }
  const label =
    status === 'partial' ? 'Partial' : status === 'incomplete' ? 'Incomplete' : 'Open'
  const badgeClass =
    status === 'partial'
      ? 'bg-amber-50 text-amber-800'
      : 'bg-gray-100 text-gray-600'
  return (
    <span
      className={`shrink-0 rounded px-1.5 font-medium ${badgeClass} ${
        variant === 'trigger' ? 'py-0.5 text-[10px]' : 'py-0.5 text-xs'
      }`}
    >
      {label}
    </span>
  )
}

const SEARCH_BLOCK_HEIGHT = 56
const LIST_MAX_PX = 240
const LIST_MAX_VIEWPORT_PX = 320
const PREFERRED_MENU_MAX_WIDTH = 512
const EDGE_MARGIN = 8

function optionMatchesSearch(opt: SelectOption, searchLower: string): boolean {
  if (opt.label.toLowerCase().includes(searchLower)) return true
  if (opt.hint?.toLowerCase().includes(searchLower)) return true
  if (opt.caption?.toLowerCase().includes(searchLower)) return true
  if (opt.id.toLowerCase().includes(searchLower)) return true
  return false
}

function optionFullTitle(opt: Pick<SelectOption, 'label' | 'hint' | 'caption'>): string {
  return [opt.label, opt.hint, opt.caption].filter(Boolean).join(' › ')
}

function findOverflowClipAncestor(start: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = start.parentElement
  while (el && el !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(el)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
      return el
    }
    el = el.parentElement
  }
  return null
}

function getVerticalClipBounds(
  trigger: HTMLElement,
  useViewportForPlacement: boolean
): { top: number; bottom: number } {
  if (useViewportForPlacement) {
    return { top: 0, bottom: window.innerHeight }
  }
  const clipEl = findOverflowClipAncestor(trigger)
  if (clipEl) {
    const r = clipEl.getBoundingClientRect()
    return { top: r.top, bottom: r.bottom }
  }
  return { top: 0, bottom: window.innerHeight }
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  allowCreate = false,
  onCreateNew,
  createLabel = 'Create',
  className = '',
  error = false,
  warning = false,
  allowClear = false,
  size = 'default',
  preserveOrder = false,
  preferBelow = false,
  useViewportForPlacement = false,
  id,
  showDetailsInTrigger = false,
  fillHeight = false,
  onKeyDown,
  onSearchChange,
}: Props) {
  const listMaxPx = useViewportForPlacement ? LIST_MAX_VIEWPORT_PX : LIST_MAX_PX
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isCreating, setIsCreating] = useState(false)
  const [openAbove, setOpenAbove] = useState(false)
  const [alignEnd, setAlignEnd] = useState(false)
  const [listMaxHeight, setListMaxHeight] = useState(LIST_MAX_PX)
  const [fixedMenuStyle, setFixedMenuStyle] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const searchRef = useRef(search)
  const creatingRef = useRef(false)
  searchRef.current = search

  const selectedOption = options.find((o) => o.id === value)

  const orderedOptions = useMemo(() => {
    if (preserveOrder) return options
    return [...options].sort((a, b) => a.label.localeCompare(b.label))
  }, [options, preserveOrder])

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return orderedOptions
    const searchLower = search.toLowerCase()
    return orderedOptions.filter((o) => optionMatchesSearch(o, searchLower))
  }, [orderedOptions, search])

  const groupedOptions = useMemo(() => {
    const groups: Record<string, SelectOption[]> = {}
    filteredOptions.forEach((opt) => {
      const group = opt.group || ''
      if (!groups[group]) groups[group] = []
      groups[group].push(opt)
    })
    if (preserveOrder) {
      const result: Record<string, SelectOption[]> = {}
      const groupOrder: string[] = []
      filteredOptions.forEach((opt) => {
        const group = opt.group || ''
        if (!groupOrder.includes(group)) groupOrder.push(group)
      })
      groupOrder.forEach((key) => {
        const opts = groups[key]
        if (opts) result[key] = opts
      })
      return result
    }
    const sortedGroups = Object.keys(groups).sort()
    const result: Record<string, SelectOption[]> = {}
    sortedGroups.forEach((key) => {
      const opts = groups[key]
      if (opts) result[key] = opts
    })
    return result
  }, [filteredOptions, preserveOrder])

  const flatFilteredOptions = useMemo(() => {
    const result: SelectOption[] = []
    Object.entries(groupedOptions).forEach(([, opts]) => {
      result.push(...opts)
    })
    return result
  }, [groupedOptions])

  const firstSelectableIndex = useMemo(() => {
    const idx = flatFilteredOptions.findIndex((opt) => !opt.disabled)
    return idx >= 0 ? idx : 0
  }, [flatFilteredOptions])

  function nextSelectableIndex(current: number, direction: 1 | -1): number {
    const total = flatFilteredOptions.length + (showCreateOption ? 1 : 0)
    if (total === 0) return 0
    let next = current
    for (let i = 0; i < total; i++) {
      next = (next + direction + total) % total
      if (showCreateOption && next === flatFilteredOptions.length) return next
      const opt = flatFilteredOptions[next]
      if (opt && !opt.disabled) return next
    }
    return current
  }

  const showCreateOption = allowCreate && search.trim() && !filteredOptions.some(
    (o) => o.label.toLowerCase() === search.toLowerCase()
  )

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setIsOpen(false)
      setSearch('')
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const menuIsPositioned = Boolean(fixedMenuStyle)
  // Only refocus when the menu opens (or first viewport position is ready).
  // Depending on the style object retriggers focus → scroll → placement forever.
  useLayoutEffect(() => {
    if (!isOpen) return
    if (useViewportForPlacement && !menuIsPositioned) return

    const focusSearch = () => inputRef.current?.focus()
    focusSearch()
    const raf = requestAnimationFrame(focusSearch)
    return () => cancelAnimationFrame(raf)
  }, [isOpen, menuIsPositioned, useViewportForPlacement])

  useEffect(() => {
    setHighlightedIndex(firstSelectableIndex)
  }, [search, firstSelectableIndex])

  useEffect(() => {
    onSearchChange?.(search)
  }, [search, onSearchChange])

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return

    function updatePlacement(e?: Event) {
      if (e) {
        const target = e.target
        if (
          target instanceof Node &&
          (menuRef.current?.contains(target) || listRef.current?.contains(target))
        ) {
          return
        }
      }
      const root = containerRef.current
      if (!root) return
      const trigger = root.getBoundingClientRect()
      const clip = getVerticalClipBounds(root, useViewportForPlacement)
      const spaceBelow = clip.bottom - trigger.bottom - EDGE_MARGIN
      const spaceAbove = trigger.top - clip.top - EDGE_MARGIN
      const fullMenu = SEARCH_BLOCK_HEIGHT + listMaxPx + 12
      const minBelowForMenu = SEARCH_BLOCK_HEIGHT + 96 + 12

      let above = false
      if (preferBelow && useViewportForPlacement) {
        const belowInsufficient = spaceBelow < minBelowForMenu
        const aboveMeaningfullyLarger = spaceAbove > spaceBelow + 80
        above = belowInsufficient && aboveMeaningfullyLarger
      } else if (preferBelow) {
        above = spaceBelow < fullMenu && spaceAbove > spaceBelow
      } else {
        above =
          spaceBelow < fullMenu &&
          (spaceAbove >= fullMenu || spaceAbove > spaceBelow)
      }

      const available = above ? spaceAbove : spaceBelow
      const listCap = Math.max(96, Math.min(listMaxPx, available - SEARCH_BLOCK_HEIGHT - 4))
      // Prefer growing past the trigger width; flip to right-align if that would overflow the viewport.
      const preferredMenuWidth = Math.min(
        PREFERRED_MENU_MAX_WIDTH,
        window.innerWidth - EDGE_MARGIN * 2
      )
      const spaceRight = window.innerWidth - trigger.left - EDGE_MARGIN
      const shouldAlignEnd =
        spaceRight < preferredMenuWidth && trigger.right > preferredMenuWidth
      setAlignEnd((prev) => (prev === shouldAlignEnd ? prev : shouldAlignEnd))

      if (useViewportForPlacement) {
        const menuWidth = Math.max(trigger.width, preferredMenuWidth)
        let left = shouldAlignEnd ? trigger.right - menuWidth : trigger.left
        left = Math.max(
          EDGE_MARGIN,
          Math.min(left, window.innerWidth - menuWidth - EDGE_MARGIN)
        )
        const nextStyle = above
          ? { bottom: window.innerHeight - trigger.top + 4, left, width: menuWidth }
          : { top: trigger.bottom + 4, left, width: menuWidth }
        setFixedMenuStyle((prev) => {
          if (
            prev &&
            prev.top === nextStyle.top &&
            prev.bottom === nextStyle.bottom &&
            prev.left === nextStyle.left &&
            prev.width === nextStyle.width
          ) {
            return prev
          }
          return nextStyle
        })
      } else {
        setFixedMenuStyle((prev) => (prev === null ? prev : null))
      }

      setOpenAbove((prev) => (prev === above ? prev : above))
      setListMaxHeight((prev) => (prev === listCap ? prev : listCap))
    }

    updatePlacement()
    const clipEl = containerRef.current ? findOverflowClipAncestor(containerRef.current) : null
    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)
    clipEl?.addEventListener('scroll', updatePlacement)
    return () => {
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
      clipEl?.removeEventListener('scroll', updatePlacement)
    }
  }, [
    isOpen,
    search,
    flatFilteredOptions.length,
    showCreateOption,
    preferBelow,
    useViewportForPlacement,
    listMaxPx,
  ])

  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.querySelector('[data-highlighted="true"]')
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex, isOpen])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => nextSelectableIndex(prev, 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => nextSelectableIndex(prev, -1))
        break
      case 'Enter':
        e.preventDefault()
        if (showCreateOption && highlightedIndex === flatFilteredOptions.length) {
          handleCreate()
        } else {
          const option = flatFilteredOptions[highlightedIndex]
          if (option && !option.disabled) {
            handleSelect(option)
          }
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
        break
      case 'Tab':
        setIsOpen(false)
        setSearch('')
        break
    }
  }

  function handleSelect(option: SelectOption) {
    if (option.disabled) return
    onChange(option.id, option.label)
    setIsOpen(false)
    setSearch('')
  }

  async function handleCreate() {
    const name = searchRef.current.trim()
    if (!onCreateNew || !name || creatingRef.current) return
    creatingRef.current = true
    setIsCreating(true)
    try {
      const newOption = await onCreateNew(name)
      if (newOption) {
        onChange(newOption.id, newOption.label)
        setIsOpen(false)
        setSearch('')
      }
    } catch (err) {
      console.error('Failed to create:', err)
    } finally {
      creatingRef.current = false
      setIsCreating(false)
    }
  }

  function handleClear(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    onChange(null, null)
    setIsOpen(false)
    setSearch('')
  }

  const showClear = allowClear && Boolean(value) && !disabled

  let optionIndex = 0

  const showTriggerDetails =
    showDetailsInTrigger &&
    selectedOption &&
    (Boolean(selectedOption.hint) || Boolean(selectedOption.caption))

  const useCompactComboboxTrigger = size === 'compact'

  const triggerClassName = (() => {
    /* .input uses display:block; !flex ensures a single row with chevron on the right */
    const heightClasses = fillHeight
      ? 'h-full min-h-9'
      : showTriggerDetails
        ? 'min-h-9'
        : 'min-h-0'
    const base =
      size === 'compact'
        ? `!flex w-full ${heightClasses} flex-nowrap items-stretch overflow-hidden rounded border border-gray-200 bg-white p-0 text-left text-sm transition-colors`
        : `input !flex w-full ${heightClasses} flex-nowrap items-center justify-between gap-2 pl-3 text-left text-sm transition-colors`
    const rightPad =
      size === 'compact'
        ? ''
        : showClear
          ? 'pr-14'
          : 'pr-10'
    const valueColor = !value ? 'text-gray-400' : 'text-gray-900'
    if (disabled) {
      if (error) {
        return `${base} ${rightPad} ${valueColor} !opacity-100 !border-red-500 ring-2 ring-red-500`
      }
      if (warning) {
        return `${base} ${rightPad} ${valueColor} !opacity-100 !border-orange-400 ring-2 ring-orange-400`
      }
      return `${base} ${rightPad} ${valueColor}`
    }
    if (error) {
      return `${base} ${rightPad} ${valueColor} !border-red-500 ring-2 ring-red-500 hover:!border-red-600 focus:!outline-none focus:!shadow-none focus:!border-red-500 focus:!ring-2 focus:!ring-red-500`
    }
    if (warning) {
      return `${base} ${rightPad} ${valueColor} !border-orange-400 ring-2 ring-orange-400 hover:!border-orange-500 focus:!outline-none focus:!shadow-none focus:!border-orange-400 focus:!ring-2 focus:!ring-orange-400`
    }
    return `${base} ${rightPad} ${valueColor} hover:!border-gray-400 focus:!outline-none`
  })()

  const triggerValueContent = showTriggerDetails ? (
    <span className="min-w-0 flex-1 text-left">
      <span
        className={`flex items-center gap-1.5 ${
          selectedOption?.status === 'complete' ? 'text-gray-500' : ''
        }`}
      >
        <span
          className={`min-w-0 whitespace-normal break-words ${
            selectedOption?.status === 'complete' ? 'text-gray-400' : ''
          }`}
        >
          {selectedOption!.label}
        </span>
        {selectedOption?.status ? (
          <OptionStatusCue status={selectedOption.status} variant="trigger" />
        ) : null}
      </span>
      {selectedOption!.hint ? (
        <span className="mt-0.5 block text-xs text-gray-500 break-words">
          {selectedOption!.hint}
        </span>
      ) : null}
      {selectedOption!.caption ? (
        <span className="mt-0.5 block text-xs text-gray-400 break-words">
          {selectedOption!.caption}
        </span>
      ) : null}
    </span>
  ) : (
    <span
      className={`flex min-w-0 flex-1 items-center gap-1.5 truncate text-left ${
        selectedOption?.status === 'complete' ? 'text-gray-500' : ''
      }`}
    >
      <span className="min-w-0 truncate">
        {selectedOption?.label || placeholder}
      </span>
      {selectedOption?.status ? (
        <OptionStatusCue status={selectedOption.status} variant="trigger" />
      ) : null}
    </span>
  )

  const dropdownMenu = (
    <>
      <div className="p-2 border-b border-gray-100">
        <input
          ref={inputRef}
          type="text"
          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Type to search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(event) => {
            onKeyDown?.(event)
            if (event.defaultPrevented) return
            handleKeyDown(event)
          }}
        />
      </div>

      <ul
        ref={listRef}
        className="overflow-y-auto"
        style={{ maxHeight: listMaxHeight }}
        role="listbox"
      >
        {Object.entries(groupedOptions).map(([group, opts]) => (
          <li key={group || '__ungrouped'}>
            {group && (
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">
                {group}
              </div>
            )}
            <ul>
              {opts.map((opt) => {
                const thisIndex = optionIndex++
                const isHighlighted = thisIndex === highlightedIndex
                const isSelected = opt.id === value
                const isDisabled = Boolean(opt.disabled)
                const hasStatus = opt.status != null
                return (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled || undefined}
                    data-highlighted={isHighlighted}
                    onClick={() => {
                      if (!opt.actions?.length) handleSelect(opt)
                    }}
                    className={`flex items-start justify-between gap-2 px-3 py-2 text-sm ${
                      isDisabled
                        ? 'cursor-default text-gray-400'
                        : `cursor-pointer ${isHighlighted ? 'bg-blue-50 text-blue-900' : ''} hover:bg-gray-50`
                    } ${isSelected && !isDisabled && !hasStatus ? 'font-medium' : ''} ${
                      hasStatus && opt.status === 'complete' && !isDisabled ? 'text-gray-400' : ''
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block whitespace-normal break-words ${
                          hasStatus && opt.status === 'complete' ? 'text-gray-400' : ''
                        } ${isSelected && !isDisabled && hasStatus && opt.status !== 'complete' ? 'font-medium' : ''}`}
                      >
                        {opt.label}
                      </span>
                      {opt.hint ? (
                        <span className="mt-0.5 block text-xs text-gray-500 break-words">
                          {opt.hint}
                        </span>
                      ) : null}
                      {opt.caption ? (
                        <span className="mt-0.5 block text-xs text-gray-400 break-words">
                          {opt.caption}
                        </span>
                      ) : null}
                      {opt.actions?.length ? (
                        <span className="mt-1 flex flex-wrap gap-2">
                          {opt.actions.map((action) => (
                            <button
                              key={action.label}
                              type="button"
                              className="text-xs font-medium text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation()
                                action.onClick()
                                setIsOpen(false)
                                setSearch('')
                              }}
                            >
                              {action.label}
                            </button>
                          ))}
                        </span>
                      ) : null}
                    </span>
                    {hasStatus ? (
                      <OptionStatusCue status={opt.status!} variant="list" />
                    ) : (
                      isSelected && (
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      )
                    )}
                  </li>
                )
              })}
            </ul>
          </li>
        ))}

        {filteredOptions.length === 0 && !showCreateOption && (
          <li className="px-3 py-4 text-sm text-gray-500 text-center">
            No matches found
          </li>
        )}

        {showCreateOption && (
          <li role="none" className="border-t border-gray-100">
            <button
              type="button"
              role="option"
              data-highlighted={highlightedIndex === flatFilteredOptions.length}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void handleCreate()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void handleCreate()
              }}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm cursor-pointer ${
                highlightedIndex === flatFilteredOptions.length
                  ? 'bg-blue-50 text-blue-900'
                  : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              {isCreating ? (
                <span className="text-gray-500">Creating...</span>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="min-w-0 whitespace-normal break-words">
                    {createLabel} "{search}"
                  </span>
                </>
              )}
            </button>
          </li>
        )}
      </ul>
    </>
  )

  return (
    <div ref={containerRef} className={`relative ${fillHeight ? 'h-full' : ''} ${className}`}>
      <button
        type="button"
        id={id}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-invalid={error || undefined}
        aria-expanded={isOpen}
        title={selectedOption ? optionFullTitle(selectedOption) : undefined}
        className={triggerClassName}
      >
        <span
          className={`flex min-w-0 flex-1 items-center ${
            useCompactComboboxTrigger ? 'px-2 py-1' : ''
          }`}
        >
          {triggerValueContent}
        </span>
        {useCompactComboboxTrigger ? (
          <>
            {showClear ? (
              <button
                type="button"
                className="flex shrink-0 items-center self-stretch border-l border-gray-200 px-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Clear selection"
                onClick={handleClear}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}
            <span
              className="flex shrink-0 items-center self-stretch border-l border-gray-200 bg-gray-50 px-2 text-gray-400"
              aria-hidden
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </span>
          </>
        ) : (
          <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        )}
      </button>

      {showClear && !useCompactComboboxTrigger ? (
        <button
          type="button"
          className="absolute top-1/2 right-7 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Clear selection"
          onClick={handleClear}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}

      {isOpen && useViewportForPlacement && fixedMenuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[200] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
              style={fixedMenuStyle}
            >
              {dropdownMenu}
            </div>,
            document.body
          )
        : null}

      {isOpen && !useViewportForPlacement ? (
        <div
          ref={menuRef}
          className={`absolute z-50 min-w-full w-max max-w-[min(32rem,calc(100vw-1rem))] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden ${
            alignEnd ? 'right-0' : 'left-0'
          } ${openAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}
        >
          {dropdownMenu}
        </div>
      ) : null}
    </div>
  )
}
